@if (store.active) {
  <section
    class="region-chat-panel"
    data-testid="region-chat-panel"
    [attr.data-project-id]="store.context?.projectId || null"
    [attr.data-file-id]="store.context?.fileId || null"
    [attr.data-page-number]="store.context?.pageNumber || null"
    [attr.data-sheet-label]="store.context?.sheetLabel || null"
    aria-labelledby="region-chat-title"
    [class.placement-right]="placement === 'right'"
    [class.placement-top]="placement === 'top'"
    [style.top.px]="layout.top"
    [style.bottom.px]="layout.bottom"
    [style.max-height.px]="layout.maxHeight"
    [class.collapsed]="collapsed"
    [class.crop-ready]="store.cropStatus === 'ready'"
    [class.has-selection]="store.polygon.length > 0"
  >
    <header class="panel-header">
      <mat-icon aria-hidden="true">highlight_alt</mat-icon>
      <h2 id="region-chat-title" class="panel-title">{{ store.reviewOnly ? 'Use in chat' : 'Select area' }}</h2>
      <button
        mat-icon-button
        type="button"
        class="panel-collapse"
        data-testid="region-chat-collapse"
        aria-controls="region-chat-body"
        [attr.aria-expanded]="!collapsed"
        [attr.aria-label]="collapsed ? 'Expand the Select area panel' : 'Collapse the Select area panel'"
        [matTooltip]="collapsed ? 'Expand' : 'Collapse'"
        (click)="toggleCollapsed()"
      >
        <mat-icon>{{ collapsed ? 'expand_more' : 'expand_less' }}</mat-icon>
      </button>
      <button
        mat-icon-button
        type="button"
        class="panel-close"
        data-testid="region-chat-cancel"
        [attr.aria-label]="store.reviewOnly ? 'Close the chat image review' : 'Leave Select area'"
        matTooltip="Cancel (Esc)"
        (click)="close()"
      >
        <mat-icon>close</mat-icon>
      </button>
    </header>

    <div id="region-chat-body" class="panel-body" [hidden]="collapsed">
      <!--
        Reviewing the chat attachment of an area that the area tool measured (Issue #2542): the
        outline is not edited here and its measurement is on the area result, so only the image
        review follows.
      -->
      @if (store.reviewOnly) {
        <p class="panel-instructions" data-testid="region-chat-review-note">
          This is the image of the area you chose. Close this panel to go back to your areas.
        </p>
      }
      @if (store.error) {
        <p class="panel-error" role="alert" data-testid="region-chat-error">
          <mat-icon aria-hidden="true">error_outline</mat-icon>
          <span>{{ store.error }}</span>
        </p>
        @if (store.cropStatus === 'error') {
          <button mat-stroked-button type="button" data-testid="region-chat-retry" (click)="retry()">Try again</button>
        }
      }

      @if (store.cropStatus === 'generating') {
        <div class="crop-progress" data-testid="region-chat-generating" role="status">
          <mat-spinner diameter="20" aria-hidden="true"></mat-spinner>
          <span>Preparing the image from the page…</span>
        </div>
        <!-- The actions stay in place, disabled, while the image is prepared (UX note 6). -->
        <div class="panel-actions">
          <button mat-flat-button color="primary" type="button" data-testid="region-chat-use-in-chat" disabled>
            <mat-icon aria-hidden="true">chat</mat-icon>
            Attach to chat
          </button>
          <button mat-stroked-button type="button" data-testid="region-chat-copy" disabled>
            <mat-icon aria-hidden="true">content_copy</mat-icon>
            Copy image
          </button>
        </div>
      }

      @if (store.cropStatus === 'outdated' && store.polygon.length) {
        <div class="crop-progress">
          <span>The image will update when you finish adjusting.</span>
          <button mat-button type="button" data-testid="region-chat-refresh" (click)="retry()">Update now</button>
        </div>
      }

      @if (store.cropStatus === 'ready' && store.crop) {
        <figure class="crop-preview" data-testid="region-chat-crop-preview">
          @if (store.cropUrl) {
            <img
              [src]="store.cropUrl"
              [alt]="previewAlt"
              data-testid="region-chat-crop-image"
              [attr.data-attachment-id]="store.crop.attachmentId"
            />
          } @else {
            <p class="crop-unavailable">The image is ready but could not be displayed.</p>
          }
          <figcaption>
            <span class="crop-label">{{ store.crop.pageRegion.label }}</span>
            <span class="crop-meta" data-testid="region-chat-crop-meta">{{ cropCaption }}</span>
            <span class="crop-legend">
              <span class="legend-swatch" aria-hidden="true"></span>
              Outlined: your selection. Faded margin: surrounding context.
            </span>
            @if (store.lowResolution) {
              <span class="crop-warning" data-testid="region-chat-low-resolution">
                This is a large area, so small text may be hard to read. Select a smaller area for detail.
              </span>
            }
          </figcaption>
        </figure>
        <div class="panel-actions">
          <button
            mat-flat-button
            color="primary"
            type="button"
            data-testid="region-chat-use-in-chat"
            [disabled]="!store.canAttach"
            (click)="useInChat()"
          >
            <mat-icon aria-hidden="true">chat</mat-icon>
            Attach to chat
          </button>
          <button mat-stroked-button type="button" data-testid="region-chat-copy" (click)="copyImage()">
            <mat-icon aria-hidden="true">content_copy</mat-icon>
            Copy image
          </button>
        </div>
      }

      @if (store.notice) {
        <p class="panel-notice" data-testid="region-chat-notice">{{ store.notice }}</p>
      }
    </div>
  </section>
} @else if (store.restored) {
  <section
    class="region-chat-panel restored"
    data-testid="region-chat-restored-panel"
    aria-labelledby="region-chat-restored-title"
    [class.placement-right]="placement === 'right'"
    [class.placement-top]="placement === 'top'"
    [style.top.px]="layout.top"
    [style.bottom.px]="layout.bottom"
    [style.max-height.px]="layout.maxHeight"
  >
    <header class="panel-header">
      <mat-icon aria-hidden="true">{{ store.restored.stale ? 'warning' : 'highlight_alt' }}</mat-icon>
      <h2 id="region-chat-restored-title" class="panel-title">Region from chat</h2>
      <button
        mat-icon-button
        type="button"
        class="panel-close"
        aria-label="Hide the region from chat"
        (click)="close()"
      >
        <mat-icon>close</mat-icon>
      </button>
    </header>
    <p class="panel-instructions">
      {{ store.restored.source.label || 'Page ' + store.restored.source.pageNumber }}
    </p>
    @if (store.restored.stale || store.restored.message) {
      <p class="panel-error" role="alert" data-testid="region-chat-stale">
        <mat-icon aria-hidden="true">warning</mat-icon>
        <span>{{ store.restored.message || 'This page has changed since the region was captured.' }}</span>
      </p>
    }
  </section>
}

<div class="visually-hidden" role="status" aria-live="polite" aria-atomic="true" data-testid="region-chat-live">
  {{ announcement }}
</div>
